import { ReactNode } from "react"
import { Root } from "react-dom/client"

export type RenderComponentCallback = (
    dataset: DOMStringMap,
    container: HTMLElement,
    root: Root
) => ReactNode

export type IContext = "chat" | "support" | "admin"

export interface IChat {
    context: IContext
    other: any,
    userInfo: IUserInfo,
    avatar?: string,
    content?: string,
    filteredTopics?: string,
    forwardMessage?: IMessage | null
    error: string,
    filter: IFilterMessagesTypes,
    helpMode?: boolean,
    editing?: boolean,
    tab?: number,
    setTab?: (v: number) => void
    setFilter: (v: IFilterMessagesTypes) => void
    setError: (v: string) => void
    setFilteredTopics: (filter: string) => void
    setForward?: (v: IMessage | null) => void
    setReplyId?: (value: IMessage | IMessagePost | null) => void
    sendMessage: (message: IMessagePost | Blob, keep?: boolean) => void | Promise<void>
    closePopup: (() => void) | null | undefined

}
export interface INotifySeenMessage {
    topic_id: number
}

export interface IMessageSeen {
    topic_id: number,
    message_id: number,
    viewer_identity_id: number,
}

export interface IMessagePost {
    topic_id: number,
    context: IContext,
    text: string,
    mime_type: string,
    filename: string,
    file_content: Blob | null,
    reply_id?: number | null,
    created_at: number,
    forward_id?: number | null,
    temporary_message_id?: number
    object_type?: IMessageObjectType | null
    object_id?: number | null
}

export interface IMessages {
    messages: IValidMessages
    errorMessages: InvalidMessages
    pendingMessages: InvalidMessages
}
export interface IValidMessages {
    [key: number]: { [key: number]: IMessage };
}
export interface InvalidMessages {
    [key: number]: { [key: number]: IMessagePost };
}


export interface IMessage {
    message_id: number,
    created_at: number,
    edited_at?: number,
    sender_id: number,
    viewer_identity_id: number,
    sender_is_me?: boolean, // Resolved locally
    sender_is_sys?: boolean, // Resolved locally
    sender_username: string,
    is_seen: boolean,
    filename: string,
    text: string,
    attachment?: string,
    mime_type?: string,
    reply_id?: number,
    reply_text?: string,
    reply_sender_fullname?: string,
    reply_sender_username?: string,
    reply_message_type?: number,
    forward_fullname?: string,
    forward_text?: string,
    file_content: File | null,
    message_type: IMessageType,
    // Object messages (needbox, product, order, ...) — see IMessageObjectData
    object_type?: IMessageObjectType | null,
    object_id?: number | null,
    needbox_product_name?: string | null,
    needbox_image?: string | null,
    // New Messages
    status?: "pending" | "error" | 'editing' | '',
    messagePost?: IMessagePost,
    edit_text?: string,
    upload_status?: "idle" | "uploading" | "done" | "error",
    upload_progress?: IUploadProgress | number,
    upload_error?: string,
}

export interface IUploadProgress {
    total_chunks: number,
    uploaded_chunks: number,
    chunk_size: number,
}

export interface IReceivedMessage extends IMessage {
    topic_id: number,
}

export interface IMessagePending extends IMessage {
    status: "pending" | "error"
    messagePost: IMessagePost
    temporary_message_id: number
}


export interface IFilterMessagesTypes {
    mime_type: string
    text: string
    shouldFilter: boolean
    dateStart?: number,
    dateEnd?: number,
}

export interface topicsResult {
    result: boolean,
    topics: ITopics
    active_topic?: number
    code: string
}
export interface ITopics {
    [key: number]: ITopic
}

export const TopicTypes = {
    CHAT: 0,
    SUPPORT: 2,
    GROUP: 3,
    CHANNEL: 4,
} as const

export type ITopicType =
    typeof TopicTypes[keyof typeof TopicTypes]

export const ParticipantTypes = {
    USER: 1,
    VENDOR: 2,
    SUPPORT: 4,
} as const

export type IParticipantType =
    typeof ParticipantTypes[keyof typeof ParticipantTypes]


export interface ITopicLastMessage {
    last_message_text: string,
    last_message_type: number,
    last_message_created_at: number,
    last_message_deleted: boolean,
}

export interface ITopic {
    topic_id: number,
    topic_name: string,
    last_seen: string,
    topic_type: ITopicType,
    created_date: number,
    last_message_text: string,
    last_message_type: number,
    last_message_created_at: number,
    last_message_deleted: boolean,
    last_message_id: number | null,
    attachment_id?: string,
    avatar_url: string,
    other_display: string,
    message_read: boolean,
    unseen_count?: number,
    participant_type: IParticipantType,
    other_participant_type?: IParticipantType,
    other_user_id?: number,
    admin_id?: number | null,
    participant_count?: number | null,
    participant_user_ids?: number[] | null,

    temporary_target_user_id?: number, // For creating new topics with buyers or vendors
    restricted?: boolean
}

export interface messagesResult {
    result: boolean,
    messages: IMessage[]
}
export interface messageResult {
    result: boolean,
    topic_id: number
    message: IMessage
}
export enum ConnectionStatus {
    CONNECTING = 0,
    OPEN = 1,
    CLOSING = 2,
    CLOSED = 3,
}

export interface TypingEvent {
    event: string;
    user_id: number,
    topicId: number;         // Room where the user is typing
    isTyping: boolean,
}
export interface UserJoinedEvent {
    type: "user_joined";
    cookie: string;
    topicId: string;
    timestamp: number;
}

export interface IChatJoinEvent {
    type: 'chat:join';
    context: IContext;
}

export interface IUser {
    user_id: number,
    username: string
    email: string
    phone: string
    fullname: string
    address: string
    state: string
    city: string
    postal_code: string
    national_id: string
    company_name: string
    role: string
    subrole: string
    status: string
    legal: string
    wp_user_id?: string
    register_date: string
    data_version: string
    img_url: string
    is_online: string
    last_seen: string
}
export interface wsIMessage {
    sender_user_id: number,
    message_id: number,
    created_at: number,
    sender_is_sys: boolean,
    is_seen: boolean,
    filename: string,
    text: string,
    attachment: string
    mime_type?: string
    reply_id?: number
    isPending?: boolean
}

export interface PostTopicResponse {
    result: boolean;
    topics: ITopics;
}

export interface IUserBasic {
    id: number;
    fullname: string;
    avatar_url: string;
    username: string;
    last_seen: number | null | boolean;
}

export interface IGroupUsers {
    staffs?: number[]
    guests?: number[]
    buyers?: number[]
    admins?: number[]
    affiliates?: number[]
}

export interface ITemporarySupportResponse {
    success: boolean;
    data?: {
        user?: {
            dusco_user_id?: number;
            wp_user_id?: number;
            name?: string;
            phone?: string;
        };
        topic_id?: number;
        jwt?: string;
        message?: string;
    };
    message?: string;
}

export const UserRoles = {
    ADMIN: 2,
    SUPER_ADMIN: 1,
    VENDOR: 3,
    BUYER: 4,
    GUEST: 5,
    STAFF: 6,
    AFFILIATE: 7,
} as const

export type IUserRole = typeof UserRoles[keyof typeof UserRoles]

export interface IUserInfo {
    userId: string | number,
    username: string,
    role: IUserRole,
    bossId: string | number | null,
}


export const MessageTypes = {
    TEXT: 0,
    IMAGE: 1,
    FILE: 2,
    VOICE: 3,
    VIDEO: 4,
    OBJECT: 5,
} as const

export type IMessageType = typeof MessageTypes[keyof typeof MessageTypes]

/**
 * Kind of external entity a message can carry (see MessageObjectType on the server).
 * Kept in sync with app/class/chat/MessageObjectType.php
 */
export const MessageObjectTypes = {
    UNKNOWN: 0,
    PRODUCT: 1,
    ORDER: 2,
    NEEDBOX: 3,
    RETURN: 4,
    WAYBILL: 5,
} as const

export type IMessageObjectType = typeof MessageObjectTypes[keyof typeof MessageObjectTypes]

/**
 * Resolved preview data for the object referenced by an OBJECT message.
 */
export interface IMessageObjectData {
    object_type: IMessageObjectType
    object_id: number
    // NEEDBOX
    needbox_product_name?: string | null
    needbox_image?: string | null
}


export interface ITopicCategories {
    support: ITopic[],
    groups: ITopic[],
    members: ITopic[],
    buyers: ITopic[],
    vendors: ITopic[],
}

export type ITabKey = 'members' | 'groups' | 'buyers' | 'vendors' | 'support' | string

export interface ITab {
    key: ITabKey,
    label: string,
    icon: ReactNode,
}

export interface ITabBarsProps {
    tabs: ITab[],
    tab: ITabKey | null,
    setTab: (key: ITabKey) => void
    context: IContext
}

export interface ITabBarItemProps {
    tab: ITab
    activeTab: ITabKey | null
    setTab: (key: ITabKey) => void
    context: IContext
}

export interface IFetchMessagesRequest {
    topic_id: number,
    before?: number | null,
    upto?: number | null,
    filter?: IFilter,
    context?: IContext,
}

export interface IUnseenCountPayload {
    userId: number
    sectionType: number
    unseenCount: number
    asSelf?: boolean
    asBoss?: boolean
}

export interface IItemSeenPayload {
    sectionType: number
    itemId: number
}

export interface INewUnseenItemPayload {
    sectionType: number
    itemId: number
    data?: unknown
}

export interface IFetchMessagesResponse {
    success: boolean,
    messages: IMessage[] | null,
    error?: string | null,
    topic?: ITopic | null,
    new_topic?: ITopic | null,
}

export interface IMessagesStore {
    mappedMessages: { [key: number]: IMessage },
    sortedIds: number[],
}



export interface AudioVisualizerProps {
    onClick: (value: React.SetStateAction<boolean>) => void,
    recording: boolean
    replyMode: IMessage | null,
    setReplyMode: (message: IMessage | null) => void
}



export interface IMessageBody {
    topic: ITopic,
    showName: boolean,
    message: IMessage,
    replyClicked: (id: number) => void,
    highlight: boolean
    setReplyMode: (message: IMessage | null) => void
    setEditMode: (message: IMessage | null) => void
}


export interface ITypeArea {
    disabled: boolean
    topic: ITopic
}

export interface IMessageEditRequest {
    topic_id: number,
    message_id: number,
    new_text: string,
}

export interface IOtherTarget {
    user_id: number,
    // fullname: string,
    // username: string,
    // avatar_url: string,
}



export interface INewTopicResponse {
    topic_sender: ITopic,
    topic_receiver: ITopic,
    receiver_id: number
    sender_id: number,
}

export const FilterTypes = {
    DOCUMENTS: 'documents',
    VIDEO: 'video',
    AUDIO: 'audio',
    IMAGE: 'image',
    DATE: 'date',
    SEARCH: 'search',
    NO_FILTER: 'no_filter',
} as const

export type IFilterType = typeof FilterTypes[keyof typeof FilterTypes]

export interface IFilter {
    type: IFilterType,
    query: string | number | null,
}

export interface ITypeAreaState {
    typedText: string
    imagePreview: string | null
    fileData: File | null
    editMode: IMessage | null
    replyMode: IMessage | null
}
// Test 